import React from 'react';

import {Box} from '@material-ui/core';
import {useSnackbar} from 'notistack';
import {useHistory} from 'react-router-dom';

import {
    EfeAccordionValues,
    Field,
    AddressServiceRequest,
    EfeResponse,
    AddressServiceRequestExtended,
} from 'commons/models';
import {
    Constants,
    HistoryRoutes,
    Messages,
    PersonTypes,
    SnackbarVariants,
    ViewKeys,
} from 'commons/constants';
import {EfeAccordion} from 'components';
import {MainContent} from 'layouts/commons';
import {useFlowFieldSearch, useSpinner} from 'hooks';
import LocationInfoForm from './location-info-form/LocationInfoForm';
import {nextStep} from 'commons/utils/Functions';
import {useFlowContext} from 'context/onboarding-flow/OnboardingFlow';
import {useMenuContext} from 'context/onboarding-menu/OnboardingMenu';
import {ReactComponent as ActionIcon} from 'assets/icons/section/action/edit.svg';
import {ClientService} from 'services';

function LocationInfo() {
    const history = useHistory();
    const {flow, updateModuleStep} = useFlowContext();
    const [fields, setFields] = React.useState<Array<Field>>(useFlowFieldSearch());
    const {menu, expedientId, fatherKycId, foreign, personType} = useMenuContext();
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [address, setAddress] = React.useState<AddressServiceRequestExtended>();
    const {enqueueSnackbar} = useSnackbar();

    function buildNationalAddressRequest(
        values: AddressServiceRequestExtended
    ): AddressServiceRequest {
        return {
            expedientId: expedientId,
            workflowId: values.workflowId,
            viewId: flow.data.viewId,
            viewFatherId: flow.data.fatherId,
            kycId: fatherKycId,
            addressId: 2,
            streetDescription: values.streetDescriptionNat,
            addressDescription: values.addressDescriptionNat,
            outdoorNumber: values.outdoorNumberNat,
            interiorNumber: values.interiorNumberNat,
            zipCode: values.zipCodeNat,
            colonyKey: values.colonyKeyNat,
            colonyDescription: values.colonyDescriptionNat,
            municipalityKey: values.municipalityKeyNat,
            municipalityDescription: values.municipalityDescriptionNat,
            stateKey: values.stateKeyNat,
            stateDescription: values.stateDescriptionNat,
            countryKey: values.countryKeyNat,
            hasNext: false,
        };
    }

    function buildNationalAddressResponse(
        values: AddressServiceRequest,
        additionalData?: AddressServiceRequest
    ): AddressServiceRequestExtended {
        let temp: any = {...values};
        if (additionalData) {
            temp = {
                ...additionalData,
                streetDescriptionNat: values.streetDescription,
                addressDescriptionNat: values.addressDescription,
                outdoorNumberNat: values.outdoorNumber,
                interiorNumberNat: values.interiorNumber,
                zipCodeNat: values.zipCode,
                colonyKeyNat: values.colonyKey,
                colonyDescriptionNat: values.colonyDescription,
                municipalityKeyNat: values.municipalityKey,
                municipalityDescriptionNat: values.municipalityDescription,
                stateKeyNat: values.stateKey,
                stateDescriptionNat: values.stateDescription,
                countryKeyNat: values.countryKey,
            };
        }
        return temp;
    }

    async function getAddress(
        overrideForeign: boolean,
        addressId: number,
        additionalData?: AddressServiceRequest
    ): Promise<AddressServiceRequestExtended | undefined> {
        let data: AddressServiceRequestExtended | undefined;
        try {
            const response: EfeResponse<AddressServiceRequest> =
                await ClientService.getPersonalAddress(
                    fatherKycId,
                    overrideForeign ? !foreign : foreign,
                    addressId
                );
            if (response && response.data) {
                data = buildNationalAddressResponse(response.data, additionalData);
            }
        } catch (error) {
            data = undefined;
        }
        return data;
    }

    async function saveAddress(request: AddressServiceRequest, national: boolean) {
        try {
            const response: EfeResponse<AddressServiceRequest> = await ClientService.saveAddress(
                request,
                foreign
            );

            if (response && response.data) {
                enqueueSnackbar(
                    national
                        ? Messages.NATIONAL_INFORMATION_SAVED_SUCCESSFULLY
                        : Messages.INFORMATION_SAVED_SUCCESSFULLY,
                    {
                        variant: SnackbarVariants.SUCCESS,
                    }
                );
                setFields([...fields]);
                const path = nextStep(flow, menu);
                if (Constants.MODULE_STEP !== path) {
                    history.push(path);
                } else {
                    updateModuleStep(true);
                    flow.data.expandKey = path;
                    setIsLoading(false);
                }
            } else {
                setFields([...fields]);
                setIsLoading(false);
            }
        } catch (error) {
            setIsLoading(false);
            setFields([...fields]);
            enqueueSnackbar(
                national
                    ? Messages.ERROR_SAVING_NATIONAL_INFORMATION
                    : Messages.ERROR_SAVING_INFORMATION,
                {
                    variant: SnackbarVariants.ERROR,
                }
            );
        }
    }

    async function onSubmit(values: AddressServiceRequestExtended) {
        setIsLoading(true);
        const tempValues: AddressServiceRequestExtended = {...values};
        const request: AddressServiceRequest = {...tempValues};
        request.kycId = fatherKycId;
        request.viewId = flow.data.viewId;
        request.expedientId = expedientId;
        request.viewFatherId = flow.data.fatherId;
        request.addressId = 1;
        await saveAddress(request, false);
        if (
            personType === PersonTypes.FISICA_ACTIVIDAD_EMPRESARIAL &&
            foreign &&
            tempValues.zipCodeNat &&
            tempValues.zipCodeNat.length > 0
        ) {
            await saveAddress(
                buildNationalAddressRequest({
                    ...tempValues,
                }),
                true
            );
        }
    }

    React.useEffect(() => {
        if (fatherKycId > 0 && ViewKeys.FISCAL_LOCATION_INFO === flow.data.expandKey) {
            setIsLoading(true);
            (async function () {
                try {
                    const data = await getAddress(false, 1);
                    if (data && data.hasNext) {
                        setAddress(await getAddress(true, 2, data));
                    } else {
                        setAddress(data);
                    }
                    setIsLoading(false);
                } catch (error) {
                    setIsLoading(false);
                }
            })();
        }
    }, []);

    const accordion: Array<EfeAccordionValues> = [
        {
            icon: <ActionIcon />,
            title: 'Datos Generales',
            reactElement: (
                <Box mt={8}>
                    {!isLoading && (
                        <LocationInfoForm
                            fields={fields}
                            address={address}
                            submitHandler={onSubmit}
                            showAdditionalAddress={
                                personType === PersonTypes.FISICA_ACTIVIDAD_EMPRESARIAL && foreign
                            }
                        />
                    )}
                </Box>
            ),
            expandIcon: true,
            disabled: false,
            expanded: ViewKeys.FISCAL_LOCATION_INFO === flow.data.expandKey,
        },
    ];

    return (
        <MainContent>
            {isLoading && useSpinner(isLoading)}
            <EfeAccordion
                values={accordion}
                onChange={() => history.push(HistoryRoutes.LOCATION_INFO)}
            />
        </MainContent>
    );
}

export default LocationInfo;
